import { useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Tag,
  message,
  Upload,
  Row,
  Col,
  Tabs,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  SearchOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { UploadProps } from 'antd/es/upload'
import dayjs from 'dayjs'
import { confirmDelete, confirmStatusChange } from '../../hooks/useConfirm'
import { productCertAPI, uploadAPI, productAPI } from '../../api'

const { Option } = Select

// 证件类型
type CertType = 'inspection' | 'customs' | 'quarantine' | 'other'

// 商品证件接口
interface ProductCert {
  id: number
  productId: number
  productName: string
  productSpec: string
  certType: CertType
  certTypeName: string
  batchNumber: string
  productionDate: string
  expiryDate: string
  fileUrl: string
  fileName: string
  fileType: string
  fileSize: number
  status: boolean
  remark: string
  uploadDate: string
}

// 商品接口（用于选择）
interface ProductOption {
  id: number
  name: string
  spec: string
  categoryName: string
}

// 证件类型配置
const certTypeMap: Record<CertType, { name: string; color: string }> = {
  inspection: { name: '检验报告', color: 'blue' },
  customs: { name: '报关单', color: 'green' },
  quarantine: { name: '检疫证明', color: 'orange' },
  other: { name: '其他证明', color: 'default' },
}

const ProductCertManagement = () => {
  const [loading, setLoading] = useState(false)
  const [certs, setCerts] = useState<ProductCert[]>([])
  const [products, setProducts] = useState<ProductOption[]>([])
  const [modalVisible, setModalVisible] = useState(false)
  const [editingCert, setEditingCert] = useState<ProductCert | null>(null)
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'inactive'>('all')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [filterProduct, setFilterProduct] = useState<number | 'all'>('all')
  const [filterCertType, setFilterCertType] = useState<CertType | 'all'>('all')
  const [uploadedFile, setUploadedFile] = useState<{ url: string; name: string; type: string; size: number } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  useEffect(() => {
    fetchCerts()
    fetchProducts()
  }, [])

  useEffect(() => {
    fetchCerts()
  }, [activeTab, filterProduct, filterCertType, searchKeyword, pagination.current, pagination.pageSize])

  const fetchCerts = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      if (activeTab === 'active') params.status = 'active'
      if (activeTab === 'inactive') params.status = 'inactive'
      if (filterProduct !== 'all') params.productId = filterProduct
      if (filterCertType !== 'all') params.certType = filterCertType
      if (searchKeyword) params.keyword = searchKeyword

      const res = await productCertAPI.getList(params)
      if (res.code === 200) {
        setCerts(res.data.list || res.data || [])
        setPagination(prev => ({ ...prev, total: res.data?.total || res.data?.length || 0 }))
      }
    } catch (error) {
      // 错误由 request 拦截器统一处理
    } finally {
      setLoading(false)
    }
  }

  const fetchProducts = async () => {
    try {
      const res = await productAPI.getList({ pageSize: 100 })
      if (res.code === 200) {
        const list = res.data.list || res.data || []
        setProducts(list.map((p: any) => ({
          id: p.id,
          name: p.name,
          spec: p.spec || '',
          categoryName: p.categoryName || '',
        })))
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  const handleAdd = () => {
    setEditingCert(null)
    setUploadedFile(null)
    form.resetFields()
    form.setFieldsValue({ status: true })
    setModalVisible(true)
  }

  const handleEdit = (record: ProductCert) => {
    setEditingCert(record)
    setUploadedFile({
      url: record.fileUrl,
      name: record.fileName,
      type: record.fileType,
      size: record.fileSize,
    })
    form.setFieldsValue({
      ...record,
      productionDate: dayjs(record.productionDate),
      expiryDate: dayjs(record.expiryDate),
    })
    setModalVisible(true)
  }

  const handleDelete = (record: ProductCert) => {
    confirmDelete(`${record.productName} - ${record.certTypeName}`, '商品管理', async () => {
      const res = await productCertAPI.delete(record.id)
      if (res.code === 200) {
        message.success('删除成功')
        fetchCerts()
      }
    })
  }

  const handleToggleStatus = (record: ProductCert, status: boolean) => {
    const targetName = `${record.productName} - ${record.certTypeName}`
    confirmStatusChange(targetName, status, '商品管理', async () => {
      const res = await productCertAPI.toggleStatus(record.id, status)
      if (res.code === 200) {
        setCerts(certs.map(c => c.id === record.id ? { ...c, status } : c))
        message.success(status ? '已启用' : '已禁用')
      }
    })
  }

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options
    const uploadFile = file as File

    try {
      const res = await uploadAPI.uploadFile(uploadFile, 'cert')
      if (res.code === 200) {
        const url = res.data.url
        setUploadedFile({
          url,
          name: uploadFile.name,
          type: uploadFile.type,
          size: uploadFile.size,
        })
        form.setFieldValue('fileUrl', url)
        form.setFieldValue('fileName', uploadFile.name)
        form.setFieldValue('fileType', uploadFile.type)
        form.setFieldValue('fileSize', uploadFile.size)
        onSuccess?.({ url })
        message.success('上传成功')
      } else {
        throw new Error(res.message || '上传失败')
      }
    } catch (error) {
      onError?.(error as Error)
      message.error('上传失败')
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      if (!uploadedFile && !editingCert) {
        message.warning('请上传证件文件')
        return
      }

      setSubmitting(true)
      const selectedProduct = products.find(p => p.id === values.productId)

      const certData = {
        ...values,
        productName: selectedProduct?.name || '',
        productSpec: selectedProduct?.spec || '',
        certTypeName: certTypeMap[values.certType as CertType]?.name || values.certType,
        productionDate: values.productionDate.format('YYYY-MM-DD'),
        expiryDate: values.expiryDate.format('YYYY-MM-DD'),
        fileUrl: uploadedFile?.url || editingCert?.fileUrl,
        fileName: uploadedFile?.name || editingCert?.fileName,
        fileType: uploadedFile?.type || editingCert?.fileType,
        fileSize: uploadedFile?.size || editingCert?.fileSize,
      }

      if (editingCert) {
        const res = await productCertAPI.update(editingCert.id, certData)
        if (res.code === 200) {
          setCerts(certs.map(c => c.id === editingCert.id ? { ...c, ...certData } : c))
          message.success('更新成功')
        }
      } else {
        const res = await productCertAPI.create(certData)
        if (res.code === 200) {
          message.success('添加成功')
          fetchCerts()
        }
      }
      setModalVisible(false)
    } catch (error) {
      // 表单验证失败或接口错误
    } finally {
      setSubmitting(false)
    }
  }

  const handleProductChange = (productId: number) => {
    const product = products.find(p => p.id === productId)
    if (product) {
      // 可以自动填充一些商品信息
    }
  }

  const getFilteredCerts = () => {
    return certs
  }

  const formatFileSize = (size: number) => {
    if (!size) return '-'
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`
    return `${(size / (1024 * 1024)).toFixed(2)} MB`
  }

  const getFileIcon = (fileType: string) => {
    if (fileType?.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4d4f' }} />
    if (fileType?.includes('image')) return <FileImageOutlined style={{ color: '#1890ff' }} />
    return <FilePdfOutlined />
  }

  const handleDownload = async (record: ProductCert) => {
    try {
      const res = await productCertAPI.download(record.id)
      const blob = new Blob([res.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = record.fileName
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      message.error('下载失败')
    }
  }

  const columns: ColumnsType<ProductCert> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: '商品信息',
      key: 'product',
      width: 200,
      render: (_: any, record: ProductCert) => (
        <div>
          <div>{record.productName}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.productSpec}</div>
        </div>
      ),
    },
    {
      title: '证件类型',
      dataIndex: 'certType',
      key: 'certType',
      width: 120,
      render: (val: CertType) => (
        <Tag color={certTypeMap[val]?.color}>{certTypeMap[val]?.name}</Tag>
      ),
    },
    {
      title: '批次号',
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      width: 150,
      ellipsis: true,
    },
    {
      title: '生产日期',
      dataIndex: 'productionDate',
      key: 'productionDate',
      width: 110,
    },
    {
      title: '有效期至',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 110,
    },
    {
      title: '文件',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 200,
      ellipsis: true,
      render: (val: string, record: ProductCert) => (
        <Space>
          {getFileIcon(record.fileType)}
          <span>{val}</span>
          <span style={{ color: '#999', fontSize: 11 }}>({formatFileSize(record.fileSize)})</span>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (val: boolean, record: ProductCert) => (
        <Switch checked={val} size="small" onChange={(checked) => handleToggleStatus(record, checked)} />
      ),
    },
    {
      title: '上传日期',
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      width: 110,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: ProductCert) => (
        <Space size="small">
          <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(record)}>
            下载
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="product-cert-page">
      <Card size="small">
        <Tabs activeKey={activeTab} onChange={setActiveTab as (key: string) => void}>
          <Tabs.TabPane tab="全部证件" key="all" />
          <Tabs.TabPane tab="已启用" key="active" />
          <Tabs.TabPane tab="已禁用" key="inactive" />
        </Tabs>

        {/* 搜索筛选栏 */}
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索商品/批次/文件名"
            prefix={<SearchOutlined />}
            style={{ width: 220 }}
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
          />
          <Select
            style={{ width: 180 }}
            value={filterProduct}
            onChange={setFilterProduct}
            allowClear
            placeholder="全部商品"
          >
            <Option value="all">全部商品</Option>
            {products.map(p => (
              <Option key={p.id} value={p.id}>{p.name} ({p.spec})</Option>
            ))}
          </Select>
          <Select
            style={{ width: 140 }}
            value={filterCertType}
            onChange={setFilterCertType as (val: CertType | 'all') => void}
          >
            <Option value="all">全部类型</Option>
            <Option value="inspection">检验报告</Option>
            <Option value="customs">报关单</Option>
            <Option value="quarantine">检疫证明</Option>
            <Option value="other">其他证明</Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchCerts}>
            搜索
          </Button>
        </Space>

        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            上传商品证件
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={getFilteredCerts()}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1500 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
          }}
        />
      </Card>

      {/* 添加/编辑证件弹窗 */}
      <Modal
        title={editingCert ? '编辑商品证件' : '上传商品证件'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="productId" label="选择商品" rules={[{ required: true, message: '请选择商品' }]}>
                <Select
                  placeholder="请选择商品"
                  onChange={handleProductChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={products.map(p => ({
                    label: `${p.name} (${p.spec}) - ${p.categoryName}`,
                    value: p.id
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="certType" label="证件类型" rules={[{ required: true, message: '请选择证件类型' }]}>
                <Select placeholder="请选择证件类型">
                  <Option value="inspection">检验报告</Option>
                  <Option value="customs">报关单</Option>
                  <Option value="quarantine">检疫证明</Option>
                  <Option value="other">其他证明</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="batchNumber" label="批次号" rules={[{ required: true, message: '请输入批次号' }]}>
                <Input placeholder="请输入批次号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="productionDate" label="生产日期" rules={[{ required: true, message: '请选择生产日期' }]}>
                <DatePicker style={{ width: '100%' }} placeholder="选择生产日期" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="expiryDate" label="有效期至" rules={[{ required: true, message: '请选择有效期' }]}>
                <DatePicker style={{ width: '100%' }} placeholder="选择有效期" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="上传证件文件" required>
            <Upload
              customRequest={handleUpload}
              maxCount={1}
              accept=".pdf,.jpg,.jpeg,.png"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
            {uploadedFile && (
              <div style={{
                marginTop: 12,
                padding: '8px 12px',
                background: '#f5f5f5',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Space>
                  {getFileIcon(uploadedFile.type)}
                  <span>{uploadedFile.name}</span>
                  <span style={{ color: '#999', fontSize: 12 }}>({formatFileSize(uploadedFile.size)})</span>
                </Space>
                <Tag color="green">已上传</Tag>
              </div>
            )}
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="请输入备注信息（选填）" maxLength={200} showCount />
          </Form.Item>

          <Form.Item name="status" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default ProductCertManagement
